import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

type BaggageCarouselSchema = components["schemas"]["BaggageCarouselSchema"];
type BaggageCarouselCreateSchema =
  components["schemas"]["BaggageCarouselCreateSchema"];
type BaggageCarouselUpdateSchema =
  components["schemas"]["BaggageCarouselUpdateSchema"];

export const useBaggageCarousels = (): UseQueryResult<
  BaggageCarouselSchema[]
> => {
  return useQuery({
    queryKey: ["baggageCarousels"],
    queryFn: async () => {
      const { data, error } = await client.get().GET("/api/baggage/carousels");
      if (data) return data;
      console.warn("Failed to fetch baggage carousels:", error);
      throw new Error("Failed to fetch baggage carousels");
    },
  });
};

export const useBaggageHalls = (): UseQueryResult<
  components["schemas"]["BaggageHallSchema"][]
> => {
  return useQuery({
    queryKey: ["baggageHalls"],
    queryFn: async () => {
      const { data, error } = await client.get().GET("/api/baggage/halls");
      if (data) return data;
      console.warn("Failed to fetch baggage halls:", error);
      throw new Error("Failed to fetch baggage halls");
    },
  });
};

export const useOAGBaggageCounters = (): UseQueryResult<string[]> => {
  return useQuery({
    queryKey: ["oagBaggageCounters"],
    queryFn: async () => {
      const { data, error } = await client
        .get()
        .GET("/api/baggage/oag-counters");
      if (data) return data;
      console.warn("Failed to fetch OAG baggage counters:", error);
      throw new Error("Failed to fetch OAG baggage counters");
    },
  });
};

export const useCreateBaggageCarousel = () => {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<BaggageCarouselSchema, Error, BaggageCarouselCreateSchema>(
    {
      mutationFn: async (payload: BaggageCarouselCreateSchema) => {
        const { data, error } = await client
          .get()
          .POST("/api/baggage/carousel", {
            body: payload,
          });

        if (error) {
          console.error("Failed to create baggage carousel:", error);
          const apiError = error as ApiError;
          let errorDetail = "Failed to create baggage carousel";
          if (apiError?.detail) {
            const detailValue = apiError.detail;
            errorDetail =
              typeof detailValue === "string"
                ? detailValue
                : Array.isArray(detailValue)
                  ? detailValue.join(", ")
                  : JSON.stringify(detailValue);
          }
          throw new Error(errorDetail);
        }

        return data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["baggageCarousels"] });
      },
    }
  );
};

export const useUpdateBaggageCarousel = () => {
  const queryClient = useQueryClient();
  type ApiError = { detail?: string | string[] | object };

  return useMutation<
    BaggageCarouselSchema,
    Error,
    { id: number } & BaggageCarouselUpdateSchema
  >({
    mutationFn: async ({ id, ...data }) => {
      const { data: responseData, error } = await client
        .get()
        .PUT("/api/baggage/carousel/{carousel_id}", {
          params: { path: { carousel_id: id } },
          body: data,
        });

      if (error) {
        console.error("Failed to update baggage carousel:", error);
        const apiError = error as ApiError;
        let errorDetail = "Failed to update baggage carousel";
        if (apiError?.detail) {
          const detailValue = apiError.detail;
          errorDetail =
            typeof detailValue === "string"
              ? detailValue
              : Array.isArray(detailValue)
                ? detailValue.join(", ")
                : JSON.stringify(detailValue);
        }
        throw new Error(errorDetail);
      }

      return responseData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["baggageCarousels"] });
    },
  });
};

export const useDeleteBaggageCarousel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data, error } = await client
        .get()
        .DELETE("/api/baggage/carousel/{carousel_id}", {
          params: { path: { carousel_id: id } },
        });

      if (error) {
        console.error("Failed to delete baggage carousel:", error);
        throw new Error("Failed to delete baggage carousel");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["baggageCarousels"] });
    },
  });
};
